import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as HelpActions from 'src/app/store/help/help.actions';
import { selectSectionCurrent, selectSectionModify } from 'src/app/store/help/help.selector';
import { SectionDataDb } from 'src/app/model/help';
import { StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	templateUrl: './helper-section.dialog.component.html',
})

export class HelperSectionDialogComponent implements OnInit, OnDestroy {
	sectionCurrent$: Observable<SectionDataDb | undefined>;
	sectionCurrentSub: Subscription | undefined;

	sectionModifySuccess$: Observable<boolean | undefined>;
	sectionModifySuccessSub: Subscription | undefined;

	sectionForm: FormGroup;
	section: SectionDataDb;
	adding = true;

	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	// design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		this.initSelectors();
		this.store.dispatch(HelpActions.resetSectionForm());
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.onResize();
		if (this.config.data !== undefined && this.config.data.sectionId !== undefined) {
			this.store.dispatch(HelpActions.getHelpSection({ sectionId: this.config.data.sectionId }));
			this.adding = false;
		} else {
			this.section = new SectionDataDb();
			this.adding = true;
		}
		this.buildForm();
		StandardNewValidator.setFirstCompile(true);
	}

	buildForm(): void {
		this.sectionForm = new FormGroup({
			'KeyHelp': new FormControl(this.section !== undefined ? this.section.KeyHelp : '', Validators.required),
			'TitleIt': new FormControl(this.section !== undefined ? this.section.TitleIt : '', Validators.required),
			'TitleEn': new FormControl(this.section !== undefined ? this.section.TitleEn : ''),
			'TitleEs': new FormControl(this.section !== undefined ? this.section.TitleEs : ''),
			'TitleDe': new FormControl(this.section !== undefined ? this.section.TitleDe : ''),
			'TitleFr': new FormControl(this.section !== undefined ? this.section.TitleFr : ''),
			'TitleNl': new FormControl(this.section !== undefined ? this.section.TitleNl : ''),
			'TitlePl': new FormControl(this.section !== undefined ? this.section.TitlePl : ''),
			'TitleRu': new FormControl(this.section !== undefined ? this.section.TitleRu : ''),
			'TitleSk': new FormControl(this.section !== undefined ? this.section.TitleSk : ''),
			'TitleHu': new FormControl(this.section !== undefined ? this.section.TitleHu : ''),
			'TitlePt': new FormControl(this.section !== undefined ? this.section.TitlePt : ''),
			'TitleSv': new FormControl(this.section !== undefined ? this.section.TitleSv : ''),
			'TitleCs': new FormControl(this.section !== undefined ? this.section.TitleCs : ''),
		});
	}

	initSelectors(): void {
		this.sectionModifySuccess$ = this.store.select(selectSectionModify);
		this.sectionCurrent$ = this.store.select(selectSectionCurrent);
	}

	subscribeAll(): void {
		this.sectionModifySuccessSub = this.sectionModifySuccess$?.subscribe(
			(rowModifySuccess) => {
				if (rowModifySuccess) {
					this.ref.close('success');
				}
			}
		);
		this.sectionCurrentSub = this.sectionCurrent$.subscribe((sectionCurrent) => {
			if (sectionCurrent !== undefined) {
				this.section = sectionCurrent;
				this.buildForm();
				this.sectionCurrentSub?.unsubscribe();
			}
		});
	}

	ngOnDestroy(): void {
		this.sectionModifySuccessSub?.unsubscribe();
	}

	isRowText(): boolean {
		return this.f.ContentType.value === 'text';
	}

	isRowVideo(): boolean {
		return this.f.ContentType.value === 'video';
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.sectionForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.sectionForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	getFlagImage(langCode: string): string {
		return this.flagImagePath + langCode + this.flagImageFormat;
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	save(): void {
		this.section = {
			...this.section,
			KeyHelp: this.f.KeyHelp.value,
			TitleIt: this.f.TitleIt.value,
			TitleEn: this.f.TitleEn.value,
			TitleEs: this.f.TitleEs.value,
			TitleDe: this.f.TitleDe.value,
			TitleFr: this.f.TitleFr.value,
			TitleNl: this.f.TitleNl.value,
			TitlePl: this.f.TitlePl.value,
			TitleRu: this.f.TitleRu.value,
			TitleSk: this.f.TitleSk.value,
			TitleHu: this.f.TitleHu.value,
			TitlePt: this.f.TitlePt.value,
			TitleSv: this.f.TitleSv.value,
			TitleCs: this.f.TitleCs.value,
		};

		if (this.section.Id) {
			this.store.dispatch(HelpActions.editHelpSection({ section: this.section }));
		} else {
			this.store.dispatch(HelpActions.addHelpSection({ section: this.section }));
		}
	}

	get f(): any {
		return this.sectionForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	showErrorRequiredMessage(type: HelperSectionDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: HelperSectionDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}
}
